/**
 * Branding utilities — ported from af/auth/lib/utils.ts + app/[locale]/layout.tsx
 *
 * Computes and injects CSS custom properties on :root so every component
 * using var(--af-branding-*) or var(--af-gradient-*) picks up the correct
 * company colours at runtime (equivalent to the server-side style prop set
 * in af/auth's root layout).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BrandingIntroduction = {
  title?: string;
  subtitle?: string;
  body?: string;
  video_url?: string;
};

export type CompanyBranding = {
  logo?: string;
  color?: { cta?: string; theme?: string };
  social?: boolean;
  background?: string;
  text_color?: string;
  header_color?: string;
  background_colors?: { primary?: string; secondary?: string };
  // Custom intro (video or rich text) — can arrive as JSON string from Viktor
  introduction?: string | BrandingIntroduction;
};

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

/**
 * Convert a hex colour to an HSL triplet string (e.g. "331, 100%, 59%").
 * Optional adjustments let you shift lightness / saturation.
 */
export function hexToHSL(hex: string, adjustments: { lightness?: number; saturation?: number } = {}): string {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  if (adjustments.lightness !== undefined) l = Math.max(0, Math.min(100, l + adjustments.lightness));
  if (adjustments.saturation !== undefined) s = Math.max(0, Math.min(100, s + adjustments.saturation));

  return `${h}, ${s}%, ${l}%`;
}

/**
 * Convert a hex colour to an RGBA string (e.g. "255, 47, 146, 0.2").
 */
export function hexToRgb(hex: string, opacity = 1): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `0, 0, 0, ${opacity}`;
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity}`;
}

// ---------------------------------------------------------------------------
// CSS variable injection
// ---------------------------------------------------------------------------

/**
 * Apply company branding as CSS custom properties on :root.
 * Mirrors the logic in af/auth → app/[locale]/layout.tsx.
 * Pass null / undefined to reset to the default pink/orange palette.
 */
export function applyBranding(branding: CompanyBranding | null | undefined): void {
  const root = document.documentElement;

  // Fallback defaults (AssessFirst brand colours)
  const cta = branding?.color?.cta || '#ff2f92';
  const theme = branding?.color?.theme || '#ff2f92';
  const primary = branding?.background_colors?.primary || '#ff2f92';
  const secondary = branding?.background_colors?.secondary || '#f59f00';

  const vars: Record<string, string> = {
    // ── CTA button ──────────────────────────────────────────────────────
    '--af-branding-cta': cta,
    '--af-branding-cta-hover': `hsl(${hexToHSL(cta, { lightness: -10 })})`,
    '--af-branding-cta-disabled': branding?.color?.cta ? `rgba(${hexToRgb(cta, 0.2)})` : '#FF97C8',
    '--af-branding-cta-active': branding?.color?.cta || '#ca2573',

    // ── AL library semantic tokens (ALButton primary, ALCheckbox, ALRadio) ──
    '--color-af-primary': cta,
    '--color-background-button-primary-default': cta,
    // @TODO remplacer ces variables par useTheme() et ainsi récupérer les couleurs du thème
    // et leurs états disabled / hover etc ne plus les hardcoder ici
    '--color-background-button-primary-hover': `hsl(${hexToHSL(cta, { lightness: -10 })})`,
    '--color-background-button-primary-pressed': `hsl(${hexToHSL(cta, { lightness: -20 })})`,
    '--color-background-button-primary-disabled': branding?.color?.cta ? `rgba(${hexToRgb(cta, 0.4)})` : '#FF97C8',
    '--color-background-checkbox-checked': cta,
    '--color-background-radio-button-checked': cta,
    // ALLoader's spinning arc — the design system pins it to af-pink-500, so an
    // unbranded page keeps the same pink as before. Note the component resolves
    // this through getComputedStyle at setup time and never re-reads it, so a
    // loader already on screen when applyBranding() runs keeps the old colour.
    '--color-background-loader-stroke': cta,

    // ── Theme ────────────────────────────────────────────────────────────
    '--af-branding-theme': theme,
    '--af-branding-theme-secondary': branding?.color?.theme ? theme : '#067c8a',

    // ── Border ───────────────────────────────────────────────────────────
    '--af-branding-border': '#f1f3f4',
    '--af-branding-border-avatar': branding?.color?.theme ? '#f1f3f4' : '#fce2b2',

    // ── Background gradient ──────────────────────────────────────────────
    '--af-branding-bg-primary': primary,
    '--af-branding-bg-secondary': secondary,

    // Raw rgba variants (used in inline styles / filter effects)
    '--af-gradient-start-rgb': `rgba(${hexToRgb(primary)})`,
    '--af-gradient-end-rgb': `rgba(${hexToRgb(secondary)})`,
    '--af-gradient-start-rgb-10': `rgba(${hexToRgb(primary, 0.1)})`,
    '--af-gradient-end-rgb-10': `rgba(${hexToRgb(secondary, 0.1)})`,
    '--af-gradient-start-rgb-20': `rgba(${hexToRgb(primary, 0.2)})`,
    '--af-gradient-end-rgb-20': `rgba(${hexToRgb(secondary, 0.2)})`,
    '--af-gradient-start-rgb-30': `rgba(${hexToRgb(primary, 0.3)})`,
    '--af-gradient-end-rgb-30': `rgba(${hexToRgb(secondary, 0.3)})`,
    '--af-gradient-start-rgb-65': `rgba(${hexToRgb(primary, 0.65)})`,
    '--af-gradient-end-rgb-65': `rgba(${hexToRgb(secondary, 0.65)})`,

    // HSL triplets — used by hsla(var(--af-gradient-start), opacity) in app.css
    '--af-gradient-start': hexToHSL(primary),
    '--af-gradient-end': hexToHSL(secondary),

    // ── Text / heading ───────────────────────────────────────────────────
    '--af-branding-text': branding?.text_color || '#2d2f33',
    '--af-branding-heading': branding?.header_color || '#484d51',

    // ── Misc ──────────────────────────────────────────────────────────────
    '--af-branding-accent': cta,
    '--af-branding-progressbar': branding?.color?.theme ? theme : '#4ebac7',
    '--af-branding-text-accent': branding?.color?.theme ? theme : '#029caf'
  };

  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}
